import { Button, Link, Popover, Stack } from '@mui/material';
import React, { useRef } from 'react';

import CopyButton from '@components/CopyButton/CopyButton';

type PopperType = 'button' | 'link';

interface PopperProps {
  /**
   * Indicates whether the popper is open or closed.
   */
  open: boolean;
  /**
   * The content to display in the button or link that triggers the popper.
   */
  buttonContent: React.ReactNode;
  /**
   * The content to display in the popper.
   */
  popoverContent: string;
  /**
   * The function to call when the button or link is clicked.
   */
  onClick: () => void;
  /**
   * The function to call when the popper is closed.
   */
  onClose: () => void;
  /**
   * The type of the button or link that triggers the popper.
   */
  type: PopperType;
  /**
   * Indicates whether the copy button should be displayed in the popper.
   * Optional. Defaults to false.
   */
  copyContent?: boolean;
}

/**
 * Renders a Popper component.
 *
 * @param {PopperProps} props - The props object containing the necessary information to render the Popper component.
 * @returns {JSX.Element} The rendered Popper component.
 */
export default function Popper({
  open,
  buttonContent,
  popoverContent,
  onClick,
  onClose,
  type,
  copyContent,
}: PopperProps): JSX.Element {
  // Ref object to store the reference to the button element.
  const anchorBtnRef = useRef<HTMLButtonElement | null>(null);
  // Ref object to store the reference to the link element.
  const anchorLinkRef = useRef<HTMLAnchorElement | null>(null);

  /**
   * Callback function to handle button click event.
   */
  const handleClick = () => onClick();

  /**
   * Callback function to handle popper close event.
   */
  const handleClose = () => onClose();

  return (
    <>
      {/* Render the button or link depending on the type prop */}
      {type === 'button' ? (
        <Button color="inherit" onClick={handleClick} ref={anchorBtnRef}>
          {buttonContent}
        </Button>
      ) : (
        <Link
          onClick={handleClick}
          ref={anchorLinkRef}
          sx={{ cursor: 'pointer' }}
        >
          {buttonContent}
        </Link>
      )}

      {/* Render the popover */}
      <Popover
        anchorEl={
          type === 'button' ? anchorBtnRef.current : anchorLinkRef.current
        }
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'bottom',
        }}
        onClose={handleClose}
        open={open}
      >
        <Stack
          direction="row"
          overflow="scroll"
          alignItems="center"
          p={1}
          fontSize={13}
        >
          {/* Render the popover content */}
          {copyContent && <CopyButton content={popoverContent} />}
          <pre>
            {/* Render the copy button if copyContent is true */}
            {popoverContent}
          </pre>
        </Stack>
      </Popover>
    </>
  );
}
